import React, { useContext, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';

import Headline from '@components/atoms/headline/headline';
import Copy from '@components/atoms/copy/copy';
import { AngleLeft, AngleRight } from '@components/icons/arrow/arrow';
import LoadingIndicator from '@components/atoms/loading-indicator/loadingIndicator';

import Popover from '../popover/popover';
import WishRadio from '../wishRadio/wishRadio';
import WishLocation from '../wishLocation/wishLocation';
import { WishIcon } from '../icons/icons';

import formDe from '../../data/wish-dialog-de';
import formEn from '../../data/wish-dialog-en';

import WishDialogHint from './components/wishDialogHint';
import StyledWishDialog from './wishDialog.style';
import { WishesContext } from '../../contexts/wishesProvider';

const WishDialog = () => {
  const {
    error,
    loading,
    wishesApiKeys,
    postWish,
    openWishDialog,
    wishesFormHint,
    setOpenWishDialog,
    setOpenEvaluation
  } = useContext(WishesContext);
  const t = useTranslations();
  const locale = useLocale();
  const [googleMapsPlacesLoaded, setGoogleMapsPlacesLoaded] = useState(null);
  const dialogData = locale === 'de' ? formDe : formEn;
  const [step, setStep] = useState(1);
  const [locationOutput, setLocationOutput] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    originalLang: locale,
    lat: 0,
    lng: 0,
    userResidence: '',
    userAgeGroup: '',
    userGender: ''
  });

  const onCloseHandle = () => {
    setStep(1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleChange = input => e => {
    setFormData({ ...formData, [input]: e.target.value });
  };

  const handleChangeSelect = input => inputValue => {
    if (googleMapsPlacesLoaded) {
      setLocationOutput(inputValue);

      setFormData({
        ...formData,
        [input]: inputValue?.label
      });
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (step === 2) {
      postWish(formData, () => {
        setStep(3);
        setLocationOutput('');
      });
    } else {
      setStep(step + 1);
    }
  };

  return (
    <StyledWishDialog>
      <Popover
        headline={t('wishes.wishDialog.headline')}
        placement="top-start"
        closeLabel={t('close')}
        trigger={{
          label: t('wishes.wishDialog.label'),
          ariaLabel: t('wishes.wishDialog.label'),
          icon: <WishIcon />
        }}
        open={openWishDialog}
        onOpen={setOpenWishDialog}
        onClose={onCloseHandle}
      >
        {step === 3 ? (
          <>
            <Copy small>{t('wishes.wishDialog.success')}</Copy>
            <div className="wishDialog__actions">
              <button
                type="button"
                className="wishDialog__next-btn"
                onClick={() => {
                  setOpenWishDialog(false);
                  setStep(1);
                  setOpenEvaluation(true);
                }}
              >
                <Headline tag="span" subline noMargin>
                  {t('wishes.wishDialog.evaluationCta')}
                </Headline>
                <AngleRight />
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <label htmlFor="title" className="wishDialog__input">
              <span hidden>{dialogData.wish.label}</span>
              <input
                type="text"
                id={dialogData.wish.id}
                name={dialogData.wish.name}
                placeholder={dialogData.wish.placeholder}
                onChange={handleChange(dialogData.wish.id)}
                readOnly={step === 2}
                value={formData.title[locale]}
                required
              />
              <div className="wishDialog__input-icon">
                <div className="wishDialog__input-icon-inner">
                  <WishIcon />
                </div>
              </div>
            </label>

            {step === 2 && (
              <>
                <fieldset>
                  <Copy tag="legend" extraSmall noMargin>
                    {dialogData.location.label}*
                  </Copy>
                  <WishLocation
                    googleMapsKey={
                      wishesApiKeys?.googleMapsApiKey ||
                      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
                      ''
                    }
                    countryRestriction={[]}
                    onSDKLoaded={setGoogleMapsPlacesLoaded}
                    inputProps={{
                      id: dialogData.location.id,
                      label: dialogData.location.label,
                      name: dialogData.location.name,
                      placeholder: dialogData.location.placeholder,
                      value: locationOutput || '',
                      onChange: handleChangeSelect(dialogData.location.name)
                    }}
                    labels={dialogData.location.labels}
                    required
                  />
                </fieldset>
                <fieldset>
                  <Copy tag="legend" extraSmall noMargin>
                    {dialogData.age.label}*
                  </Copy>
                  {dialogData.age.cells.map(item => (
                    <WishRadio
                      key={item.id}
                      label={item.label}
                      name={item.name}
                      value={item.value}
                      onChange={handleChange(item.name)}
                      required
                    />
                  ))}
                </fieldset>
                <fieldset>
                  <Copy tag="legend" extraSmall noMargin>
                    {dialogData.gender.label}*
                  </Copy>

                  {dialogData.gender.cells.map(item => (
                    <WishRadio
                      key={item.id}
                      label={item.label}
                      name={item.name}
                      value={item.value}
                      onChange={handleChange(item.name)}
                      required
                    />
                  ))}
                </fieldset>
                {wishesFormHint && (
                  <fieldset>
                    <WishDialogHint
                      hintCta={t('wishes.wishDialog.hintCta')}
                      text={wishesFormHint}
                    />
                  </fieldset>
                )}
                {error && (
                  <fieldset>
                    <Copy small noMargin className="wishDialog__error">
                      {t('wishes.wishDialog.error')}
                    </Copy>
                  </fieldset>
                )}
              </>
            )}
            <div className="wishDialog__actions">
              <button
                type="button"
                className="wishDialog__prev-btn"
                onClick={prevStep}
                disabled={step === 1}
              >
                <AngleLeft />
                <Headline tag="span" subline noMargin>
                  {dialogData.actions.prev}
                </Headline>
              </button>
              <button type="submit" className="wishDialog__next-btn" disabled={loading}>
                <Headline tag="span" subline noMargin>
                  {}
                  {loading ? (
                    <LoadingIndicator inverted />
                  ) : step === 2 ? (
                    dialogData.actions.submit
                  ) : (
                    dialogData.actions.next
                  )}
                </Headline>
                {}
                {loading ? null : step === 2 ? <WishIcon /> : <AngleRight />}
              </button>
            </div>
          </form>
        )}
      </Popover>
    </StyledWishDialog>
  );
};

export default WishDialog;
